import Loader from 'Components/Forms/Loader';
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import Firebase from './Firebase';
import * as Sentry from '@sentry/react';

interface IFirebaseContextValues {
  authenticated: boolean;
  featureFlags: { [key: string]: string | boolean };
  firebase: Firebase;
  logout: () => void;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
}

const FirebaseContext = createContext<IFirebaseContextValues>({
  authenticated: false,
  featureFlags: {},
  firebase: {} as Firebase,
  logout: () => {
    console.warn('Logout function not implemented');
  },
  changePassword: async () => {
    console.warn('Change password function not implemented');
  },
});

const FirebaseConsumer = FirebaseContext.Consumer;

const defaultConfig: { [key: string]: string | boolean } = {
  underMaintenance: false,
  documentVisibilitySetting: true,
  partnerInvitations: true,
  customLabelEnabled: true,
};

const determineFlagType = (value: string): string | boolean => {
  if (value === 'true') return true;
  if (value === 'false') return false;

  return value.toString();
};

const firebase = new Firebase();

const FirebaseProvider: FC<PropsWithChildren> = ({ children }) => {
  // Check if the emulator is running
  const isEmulator: boolean = process.env.REACT_APP_FIREBASE_EMULATOR
    ? JSON.parse(process.env.REACT_APP_FIREBASE_EMULATOR)
    : false;

  const remoteConfig = firebase.getFirebase().remoteConfig();
  const [featureFlags, setFeatureFlags] = useState(defaultConfig);
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    remoteConfig.defaultConfig = featureFlags;
    remoteConfig.settings.minimumFetchIntervalMillis = 0;

    // Only get the remote config if the application is not running the emulator ( cypress )
    // This ensure the remote config is always the same when testing to prevent flaky tests working on different envs.
    if (isEmulator === false) {
      const fetchFlags = async () => {
        await remoteConfig.fetchAndActivate();
        const remoteFlags = await remoteConfig.getAll();
        const updatedFeatureFlags: { [key: string]: string | boolean } = {};

        Object.entries(remoteFlags).forEach(([key, config]) => {
          updatedFeatureFlags[key] = determineFlagType(config.asString());
        });

        setFeatureFlags(updatedFeatureFlags);
      };

      fetchFlags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    firebase
      .getFirebase()
      .auth()
      .onAuthStateChanged(user => {
        setAuthenticated(!!user);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await firebase.getFirebase().auth().signOut();
      setAuthenticated(false);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error during Firebase logout',
        },
      });
      console.error('Error during Firebase logout:', error);
    }
  };

  const changePassword = async (oldPassword: string, newPassword: string) => {
    const auth = firebase.getFirebase().auth();
    const user = auth?.currentUser;
    if (!authenticated || !user?.email) {
      throw new Error('You must be logged in to change your password');
    }
    try {
      const credential = firebase
        .getFirebase()
        .auth.EmailAuthProvider.credential(user.email, oldPassword);
      await user.reauthenticateWithCredential(credential);
    } catch {
      throw new Error('Your current password is wrong');
    }

    try {
      await user.updatePassword(newPassword);
    } catch (error) {
      throw new Error('Something went wrong updating your password. Please try again');
    }
  };

  if (loading) return <Loader />;

  return (
    <FirebaseContext.Provider
      value={{
        firebase,
        authenticated,
        featureFlags,
        logout,
        changePassword,
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};

const useFirebase = () => useContext(FirebaseContext);

export { FirebaseConsumer, FirebaseProvider, useFirebase };

export default FirebaseContext;
